// src/pages/gt3x/Rennabmeldung.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import CustomSelect from "../../components/CustomSelect.jsx";
import PageHero from "../../components/PageHero.jsx";

const SERIES_KEY = "gt3x";
const SEASON_KEY = "";

const API_BASE = String(
    process.env.REACT_APP_RV_API_BASE || "https://api.racevision.info"
).replace(/\/$/, "");

const API_ROOT = `${API_BASE}/api/race-absences`;
const AUTH_LOGIN_URL = `${API_BASE}/api/auth/discord/login`;

function readPossibleTokenFromValue(value) {
    if (!value || typeof value !== "string") return "";

    const trimmed = value.trim();

    if (trimmed.startsWith("eyJ")) {
        return trimmed;
    }

    try {
        const parsed = JSON.parse(trimmed);

        const candidates = [
            parsed?.token,
            parsed?.access_token,
            parsed?.accessToken,
            parsed?.jwt,
            parsed?.authToken,
            parsed?.user?.token,
            parsed?.user?.access_token,
        ];

        const found = candidates.find(
            (candidate) => typeof candidate === "string" && candidate.startsWith("eyJ")
        );

        return found || "";
    } catch {
        return "";
    }
}

function getAuthToken() {
    const directKeys = [
        "rv_token",
        "rv_auth_token",
        "racevision_token",
        "token",
        "access_token",
        "authToken",
        "jwt",
        "rv_auth",
        "auth",
        "user",
    ];

    for (const key of directKeys) {
        const localValue = window.localStorage.getItem(key);
        const sessionValue = window.sessionStorage.getItem(key);

        const localToken = readPossibleTokenFromValue(localValue);
        if (localToken) return localToken;

        const sessionToken = readPossibleTokenFromValue(sessionValue);
        if (sessionToken) return sessionToken;
    }

    const storages = [window.localStorage, window.sessionStorage];

    for (const storage of storages) {
        for (let i = 0; i < storage.length; i += 1) {
            const key = storage.key(i);
            if (!key) continue;

            const value = storage.getItem(key);
            const token = readPossibleTokenFromValue(value);

            if (token) return token;
        }
    }

    return "";
}

async function apiFetch(path, options = {}) {
    const token = getAuthToken();

    if (!token) {
        const err = new Error("LOGIN_REQUIRED");
        err.status = 401;
        throw err;
    }

    const url = `${API_ROOT}${path}`;

    const res = await fetch(url, {
        ...options,
        headers: {
            Authorization: `Bearer ${token}`,
            ...(options.body ? { "Content-Type": "application/json" } : {}),
            ...(options.headers || {}),
        },
    });

    const contentType = res.headers.get("content-type") || "";
    const rawText = await res.text();

    let data = null;

    if (contentType.includes("application/json")) {
        try {
            data = rawText ? JSON.parse(rawText) : null;
        } catch {
            throw new Error(`API hat ungültiges JSON geliefert: ${url}`);
        }
    } else {
        console.error("API lieferte kein JSON:", {
            url,
            status: res.status,
            contentType,
            preview: rawText.slice(0, 300),
        });

        throw new Error(
            `API liefert kein JSON. Prüfe REACT_APP_RV_API_BASE. Aufgerufen wurde: ${url}`
        );
    }

    if (!res.ok) {
        const err = new Error(
            data?.detail ||
            data?.error ||
            `API-Fehler ${res.status}`
        );
        err.status = res.status;
        err.data = data;
        throw err;
    }

    return data;
}

function formatDateTime(value) {
    if (!value) return "–";

    let raw = String(value).trim();

    const hasTimezone =
        /z$/i.test(raw) ||
        /[+-]\d{2}:\d{2}$/.test(raw);

    if (!hasTimezone && /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(raw)) {
        raw = raw.replace(" ", "T") + "Z";
    }

    const date = new Date(raw);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleString("de-DE", {
        timeZone: "Europe/Berlin",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatDeadline(value) {
    return formatDateTime(value);
}

function AbsenceDeadlineBox({ state }) {
    const deadlines = state?.deadlines;

    if (!deadlines) return null;

    return (
        <div className="rv-info-grid" style={{ marginTop: 14 }}>
            <InfoTile
                label="Abmeldefrist"
                value={deadlines.cancel_until ? formatDeadline(deadlines.cancel_until) : "Nicht gesetzt"}
                tone={deadlines.can_cancel ? "ok" : "warn"}
            />

            <InfoTile
                label="Zurückziehen bis"
                value={deadlines.withdraw_until ? formatDeadline(deadlines.withdraw_until) : "Nicht gesetzt"}
                tone={deadlines.can_withdraw ? "ok" : "warn"}
            />

            <InfoTile
                label="Status"
                value={deadlines.can_cancel ? "Abmeldung möglich" : "Abmeldefrist abgelaufen"}
                tone={deadlines.can_cancel ? "ok" : "warn"}
            />
        </div>
    );
}

function formatDriverLabel(driver) {
    if (!driver) return "";

    const carNumber = driver.car_number ? `#${driver.car_number}` : "";
    const name = driver.driver_name || driver.name || "";

    return [carNumber, name].filter(Boolean).join(" ") || "Fahrer";
}

function StatusMessage({ type, children }) {
    if (!children) return null;

    const cls =
        type === "error"
            ? "rv-message rv-message--error"
            : "rv-message rv-message--ok";

    return (
        <div className={cls}>
            {children}
        </div>
    );
}

function TonePill({ children, tone = "info" }) {
    const cls =
        tone === "ok"
            ? "rv-status-pill rv-status-pill--ok"
            : tone === "warn"
                ? "rv-status-pill rv-status-pill--warn"
                : "rv-status-pill rv-status-pill--info";

    return <span className={cls}>{children}</span>;
}

function InfoTile({ label, value, tone = "info" }) {
    return (
        <div className={`rv-absence-tile rv-absence-tile--${tone}`}>
            <div className="rv-info-tile__label">{label}</div>
            <div className="rv-info-tile__value">{value}</div>
        </div>
    );
}

function RaceSelect({ label, value, onChange, races, disabled }) {
    return (
        <label className="rv-absence-field">
            <span className="rv-absence-field__label">{label}</span>

            <CustomSelect
                value={value !== null && value !== undefined ? String(value) : ""}
                onChange={(nextValue) => onChange?.(Number(nextValue))}
                disabled={disabled}
                allowEmpty={false}
                placeholder={disabled ? "Lade Rennen…" : "Rennen auswählen…"}
                buttonClassName="rv-select--race"
                options={(Array.isArray(races) ? races : []).map((race) => ({
                    value: String(race.race_no),
                    label: race.label || `${race.race_tag} – ${race.track_name}`,
                }))}
            />
        </label>
    );
}

function DriverSelectInline({
                                value,
                                onChange,
                                drivers,
                                disabled,
                                isAdmin,
                                ownSeasonDriver,
                            }) {
    if (!isAdmin && ownSeasonDriver) {
        return (
            <div className="rv-absence-field">
                <span className="rv-absence-field__label">Fahrer</span>

                <div className="rv-absence-driver-lock">
                    {formatDriverLabel(ownSeasonDriver)}
                </div>
            </div>
        );
    }

    return (
        <label className="rv-absence-field">
            <span className="rv-absence-field__label">Fahrer</span>

            <CustomSelect
                value={value !== null && value !== undefined ? String(value) : ""}
                onChange={(nextValue) => onChange?.(String(nextValue || ""))}
                disabled={disabled}
                allowEmpty={false}
                placeholder={disabled ? "Lade Fahrer…" : "Fahrer auswählen…"}
                options={(Array.isArray(drivers) ? drivers : []).map((driver) => ({
                    value: String(driver.season_driver_id),
                    label: formatDriverLabel(driver),
                }))}
            />
        </label>
    );
}

function CurrentUserBox({ state }) {
    const user = state?.current_user;
    const own = state?.own_season_driver;

    if (!user) return null;

    return (
        <section className="rv-modern-card rv-modern-card--accent rv-absence-user-card">
            <div className="rv-section-head">
                <div>
                    <div className="rv-section-kicker">Account</div>
                    <h2>Dein Login</h2>
                </div>

                <TonePill tone={user.is_admin ? "warn" : "info"}>
                    {user.is_admin ? "Admin" : "User"}
                </TonePill>
            </div>

            <div className="rv-info-grid">
                <InfoTile
                    label="Discord Login"
                    value={user.display_name || user.discord_id || "Discord User"}
                    tone="info"
                />

                <InfoTile
                    label="Verknüpfter Fahrer"
                    value={own ? formatDriverLabel(own) : "Nicht verknüpft"}
                    tone={own ? "ok" : "warn"}
                />
            </div>

            {!own && (
                <div className="rv-message rv-message--error rv-absence-message-space">
                    Dein Discord-Account ist noch keinem iRacing-Fahrer für diese Season zugeordnet.
                </div>
            )}
        </section>
    );
}

function AbmeldungenTable({ entries, onWithdraw, busy }) {
    return (
        <section className="rv-modern-card rv-absence-table-card rv-absence-table-card--cancelled">
            <div className="rv-section-head">
                <div>
                    <div className="rv-section-kicker">Aktive Abmeldungen</div>
                    <h2>Abgemeldete Fahrer</h2>
                </div>

                <TonePill tone={entries.length ? "warn" : "ok"}>
                    {entries.length ? `${entries.length} aktiv` : "Keine Abmeldung"}
                </TonePill>
            </div>

            <div className="rv-absence-table-wrap">
                <table className="rv-absence-table">
                    <thead>
                    <tr>
                        <th>Position</th>
                        <th>Fahrer</th>
                        <th>Timestamp</th>
                        <th>Aktion</th>
                    </tr>
                    </thead>

                    <tbody>
                    {entries.length ? (
                        entries.map((entry) => (
                            <tr key={entry.absence_id}>
                                <td data-label="Position">
                                    <span className="rv-absence-pos-pill">
                                        P{entry.grid_position}
                                    </span>
                                </td>

                                <td data-label="Fahrer">
                                    <strong>{entry.driver_name}</strong>
                                </td>

                                <td data-label="Timestamp">{formatDateTime(entry.cancelled_at)}</td>

                                <td data-label="Aktion">
                                    {entry.can_withdraw ? (
                                        <button
                                            type="button"
                                            className="btn"
                                            disabled={busy}
                                            onClick={() => onWithdraw(entry.absence_id)}
                                        >
                                            Zurückziehen
                                        </button>
                                    ) : (
                                        <span className="text-dim">
                                            Keine Berechtigung
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={4} className="rv-absence-empty-cell">
                                Für dieses Rennen hat sich aktuell kein Fahrer abgemeldet.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function WaitlistTable({ drivers }) {
    return (
        <section className="rv-modern-card rv-modern-card--accent rv-absence-table-card rv-absence-table-card--waitlist">
            <div className="rv-section-head">
                <div>
                    <div className="rv-section-kicker">Warteliste</div>
                    <h2>Startberechtigte Fahrer</h2>
                </div>

                <TonePill tone={drivers.length ? "ok" : "info"}>
                    {drivers.length ? `${drivers.length} startberechtigt` : "Keine Freigabe"}
                </TonePill>
            </div>

            <p className="rv-modern-muted rv-absence-card-text">
                Anzahl aktiver Abmeldungen regulärer Fahrer = Anzahl freier Wartelistenplätze.
            </p>

            <div className="rv-absence-table-wrap">
                <table className="rv-absence-table">
                    <thead>
                    <tr>
                        <th>Wartelistenplatz</th>
                        <th>Fahrer</th>
                        <th>Team</th>
                        <th>Auto/Klasse</th>
                    </tr>
                    </thead>

                    <tbody>
                    {drivers.length ? (
                        drivers.map((driver) => (
                            <tr key={driver.season_driver_id}>
                                <td data-label="Wartelistenplatz">
                                    <span className="rv-absence-pos-pill rv-absence-pos-pill--ok">
                                        P{driver.grid_position}
                                    </span>
                                </td>

                                <td data-label="Fahrer">
                                    <strong>{driver.driver_name}</strong>
                                </td>

                                <td data-label="Team">{driver.team_name || "–"}</td>

                                <td data-label="Auto/Klasse">
                                    {driver.car_number ? `#${driver.car_number}` : "–"}
                                    {driver.class_name ? ` / ${driver.class_name}` : ""}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={4} className="rv-absence-empty-cell">
                                Aktuell ist kein Fahrer von der Warteliste startberechtigt.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

export default function Rennabmeldung() {
    const [races, setRaces] = useState([]);
    const [autoRace, setAutoRace] = useState(null);

    const [submitRace, setSubmitRace] = useState("");
    const [viewRace, setViewRace] = useState("");

    const [drivers, setDrivers] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState("");

    const [state, setState] = useState(null);

    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const isAdmin = Boolean(state?.current_user?.is_admin);
    const ownSeasonDriver = state?.own_season_driver || null;
    const needsDriverLink = Boolean(state?.needs_driver_link);

    const submitRaceDeadlineLocked = Boolean(
        !isAdmin &&
        state?.race?.race_no &&
        Number(state.race.race_no) === Number(submitRace) &&
        state?.deadlines &&
        !state.deadlines.can_cancel
    );

    const selectedSubmitRace = useMemo(() => {
        return races.find((race) => Number(race.race_no) === Number(submitRace)) || null;
    }, [races, submitRace]);

    const selectedViewRace = useMemo(() => {
        return races.find((race) => Number(race.race_no) === Number(viewRace)) || null;
    }, [races, viewRace]);

    const cancelledDrivers = useMemo(() => {
        return Array.isArray(state?.cancelled_drivers)
            ? state.cancelled_drivers
            : [];
    }, [state]);

    const eligibleWaitlistDrivers = useMemo(() => {
        return Array.isArray(state?.eligible_waitlist_drivers)
            ? state.eligible_waitlist_drivers
            : [];
    }, [state]);

    const absenceStats = useMemo(() => {
        const maxRegular = Number(state?.season?.max_regular_starters || 0);

        return {
            cancelled: cancelledDrivers.length,
            waitlistEligible: eligibleWaitlistDrivers.length,
            maxRegular,
        };
    }, [cancelledDrivers, eligibleWaitlistDrivers, state?.season?.max_regular_starters]);

    const loadState = useCallback(async (raceNo) => {
        if (!raceNo) return null;

        const data = await apiFetch(
            `/state?series_key=${encodeURIComponent(SERIES_KEY)}&season_key=${encodeURIComponent(SEASON_KEY)}&race_no=${encodeURIComponent(raceNo)}`
        );

        setState(data);
        return data;
    }, []);

    const loadDrivers = useCallback(async () => {
        const data = await apiFetch(
            `/drivers?series_key=${encodeURIComponent(SERIES_KEY)}&season_key=${encodeURIComponent(SEASON_KEY)}&regular_only=true`
        );

        const list = Array.isArray(data?.drivers) ? data.drivers : [];

        setDrivers(list);

        if (list.length === 1) {
            setSelectedDriverId(String(list[0].season_driver_id));
        }

        return list;
    }, []);

    const reloadAll = useCallback(async () => {
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        try {
            const racesData = await apiFetch(
                `/races?series_key=${encodeURIComponent(SERIES_KEY)}&season_key=${encodeURIComponent(SEASON_KEY)}`
            );

            const raceList = Array.isArray(racesData?.races) ? racesData.races : [];
            const auto = racesData?.auto_race || raceList[0] || null;
            const autoNo = auto?.race_no || "";

            setRaces(raceList);
            setAutoRace(auto);

            setSubmitRace((prev) => prev || autoNo);
            setViewRace((prev) => prev || autoNo);

            await loadDrivers();

            if (autoNo) {
                await loadState(autoNo);
            }
        } catch (e) {
            console.error(e);

            if (e.message === "LOGIN_REQUIRED") {
                setErrorMsg("Bitte melde dich zuerst mit Discord an, um die Rennabmeldung zu nutzen.");
            } else {
                setErrorMsg(e.message || "Fehler beim Laden der Rennabmeldung.");
            }
        } finally {
            setLoading(false);
        }
    }, [loadDrivers, loadState]);

    useEffect(() => {
        reloadAll();
    }, [reloadAll]);

    useEffect(() => {
        if (!viewRace) return undefined;

        let cancelled = false;

        (async () => {
            try {
                if (!cancelled) {
                    await loadState(viewRace);
                }
            } catch (e) {
                console.error(e);

                if (!cancelled) {
                    if (e.message === "LOGIN_REQUIRED") {
                        setErrorMsg("Bitte melde dich zuerst mit Discord an, um die Rennabmeldung zu nutzen.");
                    } else {
                        setErrorMsg(e.message || "Fehler beim Laden des Rennstatus.");
                    }
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [viewRace, loadState]);

    useEffect(() => {
        if (!selectedDriverId && ownSeasonDriver?.season_driver_id) {
            setSelectedDriverId(String(ownSeasonDriver.season_driver_id));
        }
    }, [ownSeasonDriver, selectedDriverId]);

    const handleSubmit = async (e = null) => {
        e?.preventDefault?.();

        setErrorMsg("");
        setSuccessMsg("");

        if (!submitRace) {
            setErrorMsg("Bitte ein Rennen auswählen.");
            return;
        }

        if (needsDriverLink) {
            setErrorMsg(
                "Dein Discord-Account ist noch keinem iRacing-Fahrer für diese Season zugeordnet. Bitte melde dich bei der Ligaleitung."
            );
            return;
        }

        if (isAdmin && !selectedDriverId) {
            setErrorMsg("Bitte als Admin einen Fahrer auswählen.");
            return;
        }

        setBusy(true);

        try {
            const body = {
                series_key: SERIES_KEY,
                season_key: SEASON_KEY,
                race_no: Number(submitRace),
            };

            if (selectedDriverId) {
                body.season_driver_id = Number(selectedDriverId);
            }

            const data = await apiFetch("/cancel", {
                method: "POST",
                body: JSON.stringify(body),
            });

            setState(data);
            setViewRace(Number(submitRace));

            const raceLabel =
                selectedSubmitRace?.label ||
                `R${submitRace}`;

            setSuccessMsg(`Abmeldung für ${raceLabel} wurde gespeichert.`);

            if (isAdmin) {
                setSelectedDriverId("");
            }
        } catch (e2) {
            console.error(e2);
            setErrorMsg(e2.message || "Fehler beim Speichern der Abmeldung.");
        } finally {
            setBusy(false);
        }
    };

    const handleWithdraw = async (absenceId) => {
        setErrorMsg("");
        setSuccessMsg("");

        if (!viewRace || !absenceId) return;

        setBusy(true);

        try {
            const data = await apiFetch("/withdraw", {
                method: "POST",
                body: JSON.stringify({
                    series_key: SERIES_KEY,
                    season_key: SEASON_KEY,
                    race_no: Number(viewRace),
                    absence_id: Number(absenceId),
                }),
            });

            setState(data);

            const raceLabel =
                selectedViewRace?.label ||
                `R${viewRace}`;

            setSuccessMsg(`Die Abmeldung für ${raceLabel} wurde zurückgezogen.`);
        } catch (e2) {
            console.error(e2);
            setErrorMsg(e2.message || "Fehler beim Zurückziehen der Abmeldung.");
        } finally {
            setBusy(false);
        }
    };

    const showLoginBox =
        errorMsg === "Bitte melde dich zuerst mit Discord an, um die Rennabmeldung zu nutzen.";

    const selectedViewRaceLabel =
        selectedViewRace?.label ||
        (selectedViewRace
            ? `${selectedViewRace.race_tag} – ${selectedViewRace.track_name}`
            : "");

    return (
        <div className="rv-page rv-absence-page">
            <PageHero
                kicker="GT3 Xperience"
                title="Rennabmeldung"
            >
                Melde dich für einzelne Rennen ab, damit Fahrer von der Warteliste automatisch
                nachrücken können. Admins können Abmeldungen für Fahrer verwalten.
            </PageHero>

            {showLoginBox && (
                <section className="rv-modern-card rv-modern-card--accent">
                    <div className="rv-section-head">
                        <div>
                            <div className="rv-section-kicker">Login erforderlich</div>
                            <h2>Discord Login benötigt</h2>
                        </div>

                        <TonePill tone="warn">Nicht angemeldet</TonePill>
                    </div>

                    <StatusMessage type="error">{errorMsg}</StatusMessage>

                    <a
                        className="btn btn--accent rv-absence-login-btn"
                        href={AUTH_LOGIN_URL}
                    >
                        Mit Discord anmelden
                    </a>
                </section>
            )}

            {!showLoginBox && (
                <>
                    <CurrentUserBox state={state} />

                    <section className="rv-modern-card rv-modern-card--accent">
                        <div className="rv-section-head">
                            <div>
                                <div className="rv-section-kicker">Absence Manager</div>
                                <h2>Fahrer abmelden</h2>
                            </div>

                            <TonePill tone={autoRace ? "info" : "warn"}>
                                {autoRace?.label || "Race Auswahl"}
                            </TonePill>
                        </div>

                        <p className="rv-modern-muted rv-absence-card-text">
                            Wähle ein Rennen aus und speichere die Abmeldung. Fahrer von der Warteliste werden
                            anhand der aktiven Abmeldungen automatisch in der Tabelle unten berechnet.
                        </p>

                        <AbsenceDeadlineBox state={state} />

                        <div className="rv-absence-form">
                            <RaceSelect
                                label="Rennen"
                                value={submitRace}
                                onChange={setSubmitRace}
                                races={races}
                                disabled={busy || loading || !races.length}
                            />

                            <DriverSelectInline
                                value={selectedDriverId}
                                onChange={setSelectedDriverId}
                                drivers={drivers}
                                disabled={busy || loading || !drivers.length}
                                isAdmin={isAdmin}
                                ownSeasonDriver={ownSeasonDriver}
                            />

                            <button
                                type="button"
                                className="btn btn--accent rv-absence-submit-btn"
                                disabled={busy || loading || !submitRace || needsDriverLink || submitRaceDeadlineLocked}
                                onClick={handleSubmit}
                            >
                                {busy ? "Speichere…" : "Abmelden"}
                            </button>
                        </div>

                        {needsDriverLink && (
                            <StatusMessage type="error">
                                Dein Discord-Account ist noch keinem iRacing-Fahrer für diese Season zugeordnet.
                                Bitte melde dich bei der Ligaleitung.
                            </StatusMessage>
                        )}

                        <StatusMessage type="success">{successMsg}</StatusMessage>

                        <StatusMessage type="error">
                            {errorMsg && !showLoginBox ? errorMsg : ""}
                        </StatusMessage>
                    </section>

                    <section className="rv-modern-card">
                        <div className="rv-section-head">
                            <div>
                                <div className="rv-section-kicker">Race Status</div>
                                <h2>Übersicht</h2>
                            </div>

                            <RaceSelect
                                label="Anzeige"
                                value={viewRace}
                                onChange={setViewRace}
                                races={races}
                                disabled={busy || loading || !races.length}
                            />
                        </div>

                        <p className="rv-modern-muted rv-absence-card-text">
                            Wähle ein Rennen aus, um Abmeldungen und dadurch startberechtigte Wartelistenfahrer
                            zu sehen.
                            {selectedViewRaceLabel ? (
                                <>
                                    {" "}
                                    Aktuell angezeigt:{" "}
                                    <strong>{selectedViewRaceLabel}</strong>
                                </>
                            ) : null}
                        </p>

                        <div className="rv-info-grid">
                            <InfoTile
                                label="Abmeldungen"
                                value={absenceStats.cancelled}
                                tone={absenceStats.cancelled ? "warn" : "ok"}
                            />

                            <InfoTile
                                label="Warteliste startberechtigt"
                                value={absenceStats.waitlistEligible}
                                tone={absenceStats.waitlistEligible ? "ok" : "info"}
                            />

                            <InfoTile
                                label="Regelstartplätze"
                                value={absenceStats.maxRegular || "–"}
                                tone="info"
                            />
                        </div>
                    </section>

                    {loading ? (
                        <section className="rv-modern-card">
                            <div className="rv-section-kicker">Laden</div>
                            <h2>Daten werden geladen…</h2>
                            <p className="rv-modern-muted">
                                Rennstatus, Fahrer und Warteliste werden gerade geladen.
                            </p>
                        </section>
                    ) : (
                        <>
                            <AbmeldungenTable
                                entries={cancelledDrivers}
                                onWithdraw={handleWithdraw}
                                busy={busy}
                            />

                            <WaitlistTable drivers={eligibleWaitlistDrivers} />
                        </>
                    )}
                </>
            )}
        </div>
    );
}